import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../../services/adminApiService';
import { legalConfig, getMissingLegalFields, hasPendingPlaceholders } from '../../../config/legalConfig';
import {
  Settings,
  Save,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Shield,
  Mail,
  Globe,
  FileText,
  AlertTriangle,
  ExternalLink,
  Calendar,
  Lock,
  Server,
} from 'lucide-react';

export const AdminPlatformSettingsView: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'GENERAL' | 'LEGAL'>('GENERAL');
  const [settings, setSettings] = useState<any>({
    platformName: 'Adiosoft Bilişim',
    platformDomain: 'adiosoft.com',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Legal config editor state
  const [legalForm, setLegalForm] = useState({
    companyName: legalConfig.companyName,
    registeredOffice: legalConfig.registeredOffice,
    registrationCountry: legalConfig.registrationCountry,
    registrationNumber: legalConfig.registrationNumber,
    taxNumber: legalConfig.taxNumber,
    email: legalConfig.email,
    phone: legalConfig.phone,
    privacyEmail: legalConfig.privacyEmail,
    effectiveDate: legalConfig.effectiveDate,
    termsVersion: legalConfig.termsVersion,
    privacyVersion: legalConfig.privacyVersion,
    kvkkVersion: legalConfig.kvkkVersion,
    cookieVersion: legalConfig.cookieVersion,
    distanceSalesVersion: legalConfig.distanceSalesVersion,
  });

  const missingFields = getMissingLegalFields(legalConfig);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminApiService.getSettings();
      if (res.success && res.settings) {
        setSettings(res.settings);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await adminApiService.saveSettings(settings);
      if (res.success) {
        setMessage({ type: 'success', text: 'Şirket ve platform ayarları başarıyla kaydedildi.' });
      } else {
        setMessage({ type: 'error', text: res.error || 'Ayarlar kaydedilemedi.' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Hata meydana geldi.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLegal = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    // Validation: Check for empty mandatory strings
    if (!legalForm.companyName.trim()) {
      setMessage({ type: 'error', text: 'Hukuki işletmeci şirket unvanı boş bırakılamaz.' });
      setSaving(false);
      return;
    }
    if (!legalForm.termsVersion.trim() || !legalForm.distanceSalesVersion.trim()) {
      setMessage({ type: 'error', text: 'Sözleşme sürüm etiketleri boş bırakılamaz.' });
      setSaving(false);
      return;
    }

    try {
      // Persist in admin settings
      const updated = {
        ...settings,
        legalConfigOverrides: legalForm,
      };
      const res = await adminApiService.saveSettings(updated);
      if (res.success) {
        setMessage({ type: 'success', text: 'Hukuki belgeler ve sözleşme yapılandırması başarıyla kaydedildi.' });
      } else {
        setMessage({ type: 'error', text: res.error || 'Kaydedilemedi.' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Hata oluştu.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="w-5 h-5 text-slate-700" />
          <span>Şirket, Platform & Hukuki Belgeler Yapılandırması</span>
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Adiosoft Super Admin yetkileriyle platform kimliği, Getpad Inc. hukuki işletme bilgileri ve sözleşme sürümlerini yönetin.
        </p>
      </div>

      {/* Subtabs Switcher */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
        <button
          type="button"
          onClick={() => setActiveSubTab('GENERAL')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeSubTab === 'GENERAL'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Settings className="w-4 h-4" />
          <span>Genel Platform Ayarları</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('LEGAL')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 relative ${
            activeSubTab === 'LEGAL'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>Hukuki Belgeler & Sözleşmeler</span>
          {hasPendingPlaceholders() && (
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          )}
        </button>
      </div>

      {message && (
        <div
          className={`p-4 rounded-xl flex items-center gap-3 text-xs font-semibold ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          {message.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* SUBTAB 1: GENERAL PLATFORM SETTINGS */}
      {activeSubTab === 'GENERAL' && (
        <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-6">
          {/* Platform Identity */}
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-3">
              <Globe className="w-4 h-4 text-blue-600" />
              <span>Platform Kimliği</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Şirket / Platform Adı</label>
                <input
                  type="text"
                  value={settings.platformName || ''}
                  onChange={(e) => setSettings({ ...settings, platformName: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-slate-900/10 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Ana Platform Domaini</label>
                <input
                  type="text"
                  value={settings.platformDomain || ''}
                  onChange={(e) => setSettings({ ...settings, platformDomain: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-slate-900/10 focus:outline-none font-mono"
                  required
                />
              </div>
            </div>
          </div>



          {/* Super Admin Privileges notice */}
          <div className="p-4 rounded-xl bg-slate-900 text-slate-100 text-xs flex items-start gap-3">
            <Shield className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <strong className="text-white">Yetkili Yönetici Doğrulaması:</strong> Bu ayarlar doğrudan veritabanı kök düğümüne kaydedilir ve tüm platform alt modülleri tarafından anlık olarak baz alınır.
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md transition flex items-center gap-2"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>Ayarları Kaydet</span>
            </button>
          </div>
        </form>
      )}

      {/* SUBTAB 2: LEGAL DOCUMENTS & CONTRACT VERSIONING */}
      {activeSubTab === 'LEGAL' && (
        <div className="space-y-6">
          {/* Missing Placeholders Warning Notice (Section 1 compliance) */}
          {missingFields.length > 0 && (
            <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 space-y-2">
              <div className="flex items-center gap-2 font-bold text-xs">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Üretim Öncesi Tamamlanması Gereken Getpad Inc. Resmi Bilgileri ({missingFields.length})</span>
              </div>
              <p className="text-[11px] text-amber-800 leading-relaxed">
                Aşağıdaki alanlar sistemde açık placeholder olarak işaretlenmiştir. Üretimde gerçek tüzel kişilik bilgileri tamamlanmadan sahte veri yayınlanamaz:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 text-xs">
                {missingFields.map((field) => (
                  <div key={field.key} className="p-2 rounded-lg bg-white/70 border border-amber-200/80 font-mono text-[11px]">
                    <span className="text-slate-500">{field.label}:</span>{' '}
                    <strong className="text-amber-800">{field.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Internal Legal Disclaimer Notice (Section 49) */}
          <div className="p-4 rounded-xl bg-slate-100 border border-slate-200 text-slate-700 text-xs flex items-start gap-3">
            <Shield className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <strong className="text-slate-900">Yönetim Bilgilendirmesi:</strong> Bu metinler Adiosoft platformunun genel kullanımına yönelik olarak hazırlanmış taslak hukuki metinlerdir. Yürürlüğe alınmadan önce Getpad Inc.&apos;in kuruluş ülkesi, müşteri kitlesinin niteliği, vergi/mesafeli satış yapısı ve yürürlükteki mevzuat dikkate alınarak hukuk danışmanı tarafından kontrol edilmelidir.
            </div>
          </div>

          {/* 5 Public Legal Documents Status List */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
            <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-3">
              Yayınlanan Zorunlu Hukuki Belgeler (5/5)
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <strong className="text-slate-800 block font-semibold">1. Kullanım Koşulları</strong>
                  <span className="text-slate-500 text-[11px]">Sürüm: {legalForm.termsVersion} · 31 Madde</span>
                </div>
                <a
                  href="/kullanim-kosullari"
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-blue-600 hover:text-blue-700 font-semibold text-[11px] flex items-center gap-1"
                >
                  <span>Görüntüle</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <strong className="text-slate-800 block font-semibold">2. Gizlilik Politikası</strong>
                  <span className="text-slate-500 text-[11px]">Sürüm: {legalForm.privacyVersion} · 6 Kategori</span>
                </div>
                <a
                  href="/gizlilik-politikasi"
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-blue-600 hover:text-blue-700 font-semibold text-[11px] flex items-center gap-1"
                >
                  <span>Görüntüle</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <strong className="text-slate-800 block font-semibold">3. KVKK Aydınlatma Metni</strong>
                  <span className="text-slate-500 text-[11px]">Sürüm: {legalForm.kvkkVersion} · 6698 m.10 Uyumlu</span>
                </div>
                <a
                  href="/kvkk-aydinlatma-metni"
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-blue-600 hover:text-blue-700 font-semibold text-[11px] flex items-center gap-1"
                >
                  <span>Görüntüle</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <strong className="text-slate-800 block font-semibold">4. Çerez Politikası</strong>
                  <span className="text-slate-500 text-[11px]">Sürüm: {legalForm.cookieVersion} · Consent Banner</span>
                </div>
                <a
                  href="/cerez-politikasi"
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-blue-600 hover:text-blue-700 font-semibold text-[11px] flex items-center gap-1"
                >
                  <span>Görüntüle</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className="sm:col-span-2 p-3.5 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <strong className="text-slate-800 block font-semibold">5. Mesafeli Satış Sözleşmesi ve Ön Bilgilendirme Formu</strong>
                  <span className="text-slate-500 text-[11px]">Sürüm: {legalForm.distanceSalesVersion} · 6502 Kanun & Cayma İstisnaları</span>
                </div>
                <a
                  href="/mesafeli-satis-ve-on-bilgilendirme"
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-blue-600 hover:text-blue-700 font-semibold text-[11px] flex items-center gap-1"
                >
                  <span>Görüntüle</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          {/* Legal Form Inputs */}
          <form onSubmit={handleSaveLegal} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-6">
            <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-3">
              Getpad Inc. Hukuki Parametreleri & Sürüm Bilgileri
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Şirket Resmi Unvanı</label>
                <input
                  type="text"
                  value={legalForm.companyName}
                  onChange={(e) => setLegalForm({ ...legalForm, companyName: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Kayıt Ülkesi</label>
                <input
                  type="text"
                  value={legalForm.registrationCountry}
                  onChange={(e) => setLegalForm({ ...legalForm, registrationCountry: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Sicil / Kayıt Numarası</label>
                <input
                  type="text"
                  value={legalForm.registrationNumber}
                  onChange={(e) => setLegalForm({ ...legalForm, registrationNumber: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Vergi Numarası / Dairesi</label>
                <input
                  type="text"
                  value={legalForm.taxNumber}
                  onChange={(e) => setLegalForm({ ...legalForm, taxNumber: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Resmi Şirket E-Postası</label>
                <input
                  type="text"
                  value={legalForm.email}
                  onChange={(e) => setLegalForm({ ...legalForm, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">KVKK / Gizlilik E-Postası</label>
                <input
                  type="text"
                  value={legalForm.privacyEmail}
                  onChange={(e) => setLegalForm({ ...legalForm, privacyEmail: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block font-semibold text-slate-700 mb-1">Resmi Merkez Adresi</label>
                <input
                  type="text"
                  value={legalForm.registeredOffice}
                  onChange={(e) => setLegalForm({ ...legalForm, registeredOffice: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Kullanım Koşulları Sürüm Kodu</label>
                <input
                  type="text"
                  value={legalForm.termsVersion}
                  onChange={(e) => setLegalForm({ ...legalForm, termsVersion: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Mesafeli Satış Sürüm Kodu</label>
                <input
                  type="text"
                  value={legalForm.distanceSalesVersion}
                  onChange={(e) => setLegalForm({ ...legalForm, distanceSalesVersion: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md transition flex items-center gap-2"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>Hukuki Bilgileri Kaydet</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
export default AdminPlatformSettingsView;
